
/**
 * Module dependencies.
 */

import { ReactNode, useCallback, useState } from 'react';
import { color } from 'react-components/styles';
import { ifProp, prop, switchProp, theme } from 'styled-tools';
import styled, { css } from 'styled-components';

/**
 * Export `Placement` type.
 */

export type Placement = 'bottom' | 'left' | 'right' | 'top';

/**
 * Export `ColorTheme` type.
 */

export type ColorTheme = 'secondary' | 'white';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  className?: string;
  colorTheme?: ColorTheme;
  content: ReactNode;
  inline?: boolean;
  minWidth?: string;
  placement?: Placement;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: flex;
  position: relative;
`;

/**
 * `ElementWrapper` styled component.
 */

const ElementWrapper = styled.div`
  display: inline-block;
`;

/**
 * `PopUp` styled component.
 */

const PopUp = styled.div<{ colorTheme: ColorTheme; inline?: boolean; minWidth?: string; placement: Placement }>`
  border-radius: ${theme('dimensions.borderRadius')}px;
  font-size: 10px;
  line-height: 1.2em;
  min-width: ${prop('minWidth', 'initial')};
  padding: 4px 8px;
  position: absolute;
  z-index: ${theme('zIndex.tooltip')};

  ${ifProp('inline', css`
    white-space: nowrap;
  `, css`
    max-width: 200px;
    white-space: no-break;
  `)}

  ${switchProp('colorTheme', {
    secondary: css`
      background-color: ${color('gray600')};
      box-shadow: 0 0 4px ${color.transparentize('black', 0.25)};
      color: ${color('white')};
    `,
    white: css`
      background-color: ${color('white')};
      box-shadow: 0 0 4px ${color.transparentize('black', 0.08)};
      color: ${color('gray600')};
    `
  })}

  ${switchProp('placement', {
    bottom: css`
      left: 50%;
      margin-top: 4px;
      top: 100%;
      transform: translateX(-50%);
    `,
    left: css`
      margin-right: 4px;
      right: 100%;
      top: 50%;
      transform: translateY(-50%);
    `,
    right: css`
      left: 100%;
      margin-left: 4px;
      top: 50%;
      transform: translateY(-50%);
    `,
    top: css`
      bottom: 100%;
      left: 50%;
      margin-bottom: 4px;
      transform: translateX(-50%);
    `
  })}
`;

/**
 * Export `Tooltip` component.
 */

export function Tooltip(props: Props): JSX.Element {
  const { children, className, colorTheme = 'secondary', content, inline, minWidth, placement = 'right' } = props;
  const [visible, setVisible] = useState<boolean>(false);
  const handleMouseLeave = useCallback(() => setVisible(false), [setVisible]);
  const handleMouseOver = useCallback(() => setVisible(true), [setVisible]);

  return (
    <Wrapper onMouseLeave={handleMouseLeave}>
      <ElementWrapper
        className={className}
        onMouseOver={handleMouseOver}
      >
        {children}
      </ElementWrapper>

      {visible && (
        <PopUp
          colorTheme={colorTheme}
          inline={inline}
          minWidth={minWidth}
          placement={placement}
        >
          {content}
        </PopUp>
      )}
    </Wrapper>
  );
}
